import api, { UPLOAD_REQUEST_TIMEOUT_MS } from "@/services/api";
import type {
  ContractPreviewResponse,
  DisclosureInvalidateResult,
  DocumentCatalog,
  DocumentRequest,
  DownloadLinkRevokeResult,
  DownloadLinkRow,
  DraftEvaluation,
  DraftPreviewRequest,
  FlowDetail,
  FlowDocument,
  FlowSummary,
  FlowType,
  IdResetResult,
  OneOffBuilderToken,
  OneOffTemplate,
  OneOffUploads,
  SendRequest,
} from "../types";

/** Staff prefix under the `api` client's `/api/v1` base. */
export const merchantFlowsPath = (borrowerId: number, suffix = ""): string =>
  `/borrowers/${borrowerId}/merchant-flows/${suffix}`;

/** Typed client for the staff endpoints S2–S13. */
export const merchantFlowService = {
  /** S2. Omit `flowType` for the first type this user may send. */
  defaults: async (borrowerId: number, flowType?: FlowType | null): Promise<DraftEvaluation> => {
    const response = await api.get<DraftEvaluation>(merchantFlowsPath(borrowerId, "defaults/"), {
      params: flowType ? { flow_type: flowType } : undefined,
    });
    return response.data;
  },

  /** S3. Same `DraftEvaluation` shape as S2; omit invalidate — send-ux owns it. */
  preview: async (borrowerId: number, body: DraftPreviewRequest): Promise<DraftEvaluation> => {
    const response = await api.post<DraftEvaluation>(merchantFlowsPath(borrowerId, "preview/"), {
      flow_type: body.flow_type,
      config: body.config,
    });
    return response.data;
  },

  /** S5. Newest first; never includes the link or the secret. */
  list: async (borrowerId: number): Promise<FlowSummary[]> => {
    const response = await api.get<FlowSummary[]>(merchantFlowsPath(borrowerId));
    return response.data;
  },

  /** S6. */
  detail: async (borrowerId: number, flowId: number): Promise<FlowDetail> => {
    const response = await api.get<FlowDetail>(merchantFlowsPath(borrowerId, `${flowId}/`));
    return response.data;
  },

  /** S4. Rejects with `409 reprompt` when the assessment fingerprint is stale. */
  send: async (borrowerId: number, body: SendRequest | DocumentRequest): Promise<FlowDetail> => {
    const response = await api.post<FlowDetail>(merchantFlowsPath(borrowerId), body);
    return response.data;
  },

  /** Mint the flow's contract for staff DocuSeal preview (no flow, no invite). */
  createContractPreview: async (
    borrowerId: number,
    body: SendRequest
  ): Promise<ContractPreviewResponse> => {
    const response = await api.post<ContractPreviewResponse>(
      merchantFlowsPath(borrowerId, "contract-preview/"),
      body
    );
    return response.data;
  },

  /** The PDF a document with no fields previews (and emails): fetched as a blob, never linked. */
  documentPdf: async (borrowerId: number, documentId: number): Promise<Blob> => {
    const response = await api.get<Blob>(
      merchantFlowsPath(borrowerId, `document-pdf/${documentId}/`),
      { responseType: "blob" }
    );
    return response.data;
  },

  /** Re-mint an open preview's embed credential once its token expires. */
  refreshContractPreview: async (
    borrowerId: number,
    submissionId: number
  ): Promise<ContractPreviewResponse> => {
    const response = await api.post<ContractPreviewResponse>(
      merchantFlowsPath(borrowerId, `contract-preview/${submissionId}/refresh/`),
      {}
    );
    return response.data;
  },

  /** Replace a filled preview with one pre-answered from DocuSeal, so staff can correct it. */
  editContractPreview: async (
    borrowerId: number,
    submissionId: number,
    body: SendRequest
  ): Promise<ContractPreviewResponse> => {
    const response = await api.post<ContractPreviewResponse>(
      merchantFlowsPath(borrowerId, `contract-preview/${submissionId}/edit/`),
      body
    );
    return response.data;
  },

  /**
   * Void a staff preview mint that was never confirmed into a flow. With
   * `answers`, resolves to its DocuSeal answers for a re-mint to pre-answer.
   */
  cancelContractPreview: async (
    borrowerId: number,
    submissionId: number,
    { answers = false }: { answers?: boolean } = {}
  ): Promise<Record<string, string> | undefined> => {
    const response = await api.post<{ field_defaults?: Record<string, string> }>(
      merchantFlowsPath(borrowerId, `contract-preview/${submissionId}/cancel/`),
      answers ? { answers: true } : {}
    );
    return response.data?.field_defaults;
  },

  /** The document types staff can request. */
  documentTypes: async (borrowerId: number): Promise<DocumentCatalog> => {
    const response = await api.get<DocumentCatalog>(
      merchantFlowsPath(borrowerId, "document-types/")
    );
    return response.data;
  },

  /** The files the merchant uploaded in one flow. */
  documents: async (borrowerId: number, flowId: number): Promise<FlowDocument[]> => {
    const response = await api.get<FlowDocument[]>(
      merchantFlowsPath(borrowerId, `${flowId}/documents/`)
    );
    return response.data;
  },

  /** Custom upload: the PDF becomes this deal's unsent one-off contract. */
  uploadOneOff: async (borrowerId: number, file: File): Promise<OneOffTemplate> => {
    const body = new FormData();
    body.append("file", file);
    const response = await api.post<OneOffTemplate>(
      merchantFlowsPath(borrowerId, "one-off-templates/"),
      body,
      {
        timeout: UPLOAD_REQUEST_TIMEOUT_MS,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  },

  /** This deal's uploads, each Document-only chip re-read live (the card shows cached ones first). */
  oneOffUploads: async (borrowerId: number): Promise<OneOffUploads> => {
    const response = await api.get<OneOffUploads>(
      merchantFlowsPath(borrowerId, "one-off-templates/")
    );
    return response.data;
  },

  /** Builder JWT to place the one-off's fields. */
  oneOffBuilderToken: async (
    borrowerId: number,
    documentId: number
  ): Promise<OneOffBuilderToken> => {
    const response = await api.post<OneOffBuilderToken>(
      merchantFlowsPath(borrowerId, `one-off-templates/${documentId}/builder-token/`),
      {}
    );
    return response.data;
  },

  /** Discard an unsent one-off: its template is archived. */
  discardOneOff: async (borrowerId: number, documentId: number): Promise<void> => {
    await api.delete(merchantFlowsPath(borrowerId, `one-off-templates/${documentId}/`));
  },

  /** S7. */
  resend: async (borrowerId: number, flowId: number): Promise<FlowSummary> => {
    const response = await api.post<FlowSummary>(
      merchantFlowsPath(borrowerId, `${flowId}/resend/`),
      {}
    );
    return response.data;
  },

  /** S8. */
  invalidate: async (borrowerId: number, flowId: number): Promise<FlowSummary> => {
    const response = await api.post<FlowSummary>(
      merchantFlowsPath(borrowerId, `${flowId}/invalidate/`),
      {}
    );
    return response.data;
  },

  /** S9. */
  invalidateDisclosure: async (borrowerId: number): Promise<DisclosureInvalidateResult> => {
    const response = await api.post<DisclosureInvalidateResult>(
      merchantFlowsPath(borrowerId, "disclosure-invalidate/"),
      {}
    );
    return response.data;
  },

  /** S10. */
  resetId: async (borrowerId: number): Promise<IdResetResult> => {
    const response = await api.post<IdResetResult>(merchantFlowsPath(borrowerId, "id-reset/"), {});
    return response.data;
  },

  /** S11. Rows never include the download URL. */
  listDownloadLinks: async (borrowerId: number, flowId: number): Promise<DownloadLinkRow[]> => {
    const response = await api.get<DownloadLinkRow[]>(
      merchantFlowsPath(borrowerId, `${flowId}/download-links/`)
    );
    return response.data;
  },

  /** S13. Idempotent. */
  revokeDownloadLink: async (
    borrowerId: number,
    flowId: number,
    linkId: number
  ): Promise<DownloadLinkRevokeResult> => {
    const response = await api.post<DownloadLinkRevokeResult>(
      merchantFlowsPath(borrowerId, `${flowId}/download-links/${linkId}/revoke/`),
      {}
    );
    return response.data;
  },
};
